// ===========================================
// Hybrid Pattern Configuration Component
// ===========================================

import { useEffect, useMemo } from 'react';
import {
  Grid,
  Typography,
  Divider,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Alert,
  Box,
  Chip,
  CircularProgress,
  Skeleton,
} from '@mui/material';
import type { SelectChangeEvent } from '@mui/material/Select';
import { LessonFormData, HybridCombination } from './lessons.types';
import { WEEK_NUMBERS } from './lessons.constants';
import { useBandLessons, useBandWeeksPreview } from '../../../hooks/useLessons';
import { computeBandCoverage, resolveTermWeekOptions, termLengthsSentence } from './lessons.helpers';

interface HybridPatternConfigProps {
  /** Disable every control (an all-locked class is read-only). Default false. */
  disabled?: boolean;
  formData: LessonFormData;
  onFormDataChange: (data: Partial<LessonFormData>) => void;
  /** The primary termId used to populate the band selector + compute the weeks preview. */
  termId: string;
  /** All terms this hybrid class spans (for the per-term band-availability signal). */
  termIds?: string[];
  /** id → name for the selected terms, to label the availability chips. */
  termNames?: Record<string, string>;
  /**
   * id → Monday-anchored week count for each selected term (`termWeekCount`).
   * When supplied, the pickers offer weeks up to the LONGEST selected term and
   * caption any week that some selected terms lack ("not in Term 4 2026") —
   * never disabled, because week 11 may be right for Term 2 even though Term 4
   * will drop it (the server clamps per term and says so on save). Absent →
   * the transport ceiling (1–15), the pre-2026-09-23 behaviour. A term longer
   * than 15 weeks is capped at 15 here, matching the validator's ceiling.
   * A selected term missing from this map is ignored by the bound and the
   * captions (fail-open) — `LessonDialog` builds it from the full terms list.
   */
  termWeeks?: Record<string, number>;
  /**
   * The class window in the ANCHOR term (`termId`), when it has one: weeks outside it are captioned
   * "not scheduled in {term}" but stay selectable (they still apply to the other terms). Absent =
   * a full-term class, no caption.
   */
  anchorWindow?: { termName: string; first: number; last: number } | null;
}

// ===========================================
// LABEL HELPERS
// ===========================================

function getPrimaryWeeksLabel(combo: HybridCombination): string {
  if (combo === 'INDIVIDUAL_BAND') return 'Individual Weeks';
  return 'Group Weeks'; // GROUP_INDIVIDUAL and GROUP_BAND
}

function getSecondaryWeeksLabel(combo: HybridCombination): string {
  if (combo === 'GROUP_INDIVIDUAL') return 'Individual Weeks';
  return 'Band Weeks';
}

const HYBRID_COMBINATION_OPTIONS: { value: HybridCombination; label: string }[] = [
  { value: 'GROUP_INDIVIDUAL', label: 'Group + Individual' },
  { value: 'GROUP_BAND', label: 'Group + Band' },
  { value: 'INDIVIDUAL_BAND', label: 'Individual + Band' },
];

// ===========================================
// COMPONENT
// ===========================================

export default function HybridPatternConfig({
  formData,
  onFormDataChange,
  termId,
  termIds = [],
  termNames = {},
  termWeeks,
  anchorWindow,
  disabled = false,
}: HybridPatternConfigProps) {
  const combo = formData.hybridCombination;
  const hasBand = combo !== 'GROUP_INDIVIDUAL';

  // Week options are bounded by the LONGEST selected term (#168 / Phase E). A week
  // that a shorter selected term lacks is offered but captioned, and one Alert
  // says which terms differ — the server drops it from those terms on save and
  // reports it; nothing here has to guess.
  // The rule lives in `resolveTermWeekOptions` (lessons.helpers) — the band picker applies
  // the identical one (#173 Phase 3), and two copies would drift invisibly: both render a
  // plausible week list either way.
  const { selectedTermWeeks, weekOptions, termLengthsDiffer, weekLabel } = useMemo(
    () => resolveTermWeekOptions(WEEK_NUMBERS, termIds, termId, termNames, termWeeks, anchorWindow),
    [termIds, termId, termNames, termWeeks, anchorWindow]
  );

  // Fetch band lessons only when a band combo is selected AND a term is chosen.
  // Without a termId the API returns one row per term for each band lesson, causing duplicates.
  const hasTerm = Boolean(termId);
  const {
    data: bandLessons = [],
    isLoading: isBandLessonsLoading,
  } = useBandLessons(termId || undefined, hasBand && hasTerm);

  // For a MULTI-TERM hybrid class the band selector lists the primary term's bands, but the
  // chosen band must exist (by identity) in every selected term or that term becomes
  // "Band: TBD". Fetch ALL bands (no term filter) and compute which selected terms the
  // chosen band's identity covers. Only needed when >1 term is selected and a band is chosen.
  const isMultiTerm = termIds.length > 1;
  const {
    data: allBands = [],
  } = useBandLessons(undefined, hasBand && isMultiTerm && Boolean(formData.bandLessonId));

  // Pure, unit-tested coverage computation (identity key kept in lock-step with the backend
  // resolver). Memoised so it doesn't re-scan allBands on every unrelated render.
  const bandCoverage = useMemo(
    () => computeBandCoverage(allBands, formData.bandLessonId, termIds),
    [allBands, formData.bandLessonId, termIds]
  );

  // Auto-preview band weeks whenever the band lesson or term changes
  const {
    data: bandWeeksPreview,
    isLoading: isBandWeeksLoading,
  } = useBandWeeksPreview(formData.bandLessonId, termId);

  // Sync server-calculated band weeks back into form state
  useEffect(() => {
    if (bandWeeksPreview !== undefined) {
      onFormDataChange({ bandWeeks: bandWeeksPreview });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bandWeeksPreview]);

  // ===========================================
  // EVENT HANDLERS
  // ===========================================

  const handleCombinationChange = (e: SelectChangeEvent<HybridCombination>) => {
    const newCombo = e.target.value as HybridCombination;
    onFormDataChange({
      hybridCombination: newCombo,
      groupWeeks: [],
      individualWeeks: [],
      bandLessonId: '',
      bandWeeks: [],
    });
  };

  const handleMultiSelectChange = (field: 'groupWeeks' | 'individualWeeks') =>
    (event: SelectChangeEvent<number[]>) => {
      const value = event.target.value;
      onFormDataChange({
        [field]: typeof value === 'string' ? value.split(',').map(Number) : value,
      });
    };

  const handleBandLessonChange = (e: SelectChangeEvent<string>) => {
    onFormDataChange({
      bandLessonId: e.target.value,
      bandWeeks: [], // will be recalculated via useBandWeeksPreview
    });
  };

  // ===========================================
  // RENDER
  // ===========================================

  return (
    <>
      <Grid item xs={12}>
        <Divider sx={{ my: 1 }} />
        <Typography variant="subtitle2" color="text.secondary" gutterBottom>
          Hybrid Pattern Configuration
        </Typography>
        <Typography variant="caption" color="text.secondary">
          Configure which weeks are group, individual, or band sessions
        </Typography>
      </Grid>

      {/* Hybrid Combination */}
      <Grid item xs={12} sm={6}>
        <FormControl fullWidth required disabled={disabled}>
          <InputLabel>Hybrid Combination</InputLabel>
          <Select
            value={combo}
            label="Hybrid Combination"
            onChange={handleCombinationChange}
          >
            {HYBRID_COMBINATION_OPTIONS.map((opt) => (
              <MenuItem key={opt.value} value={opt.value}>
                {opt.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Grid>

      {/* Pattern Type */}
      <Grid item xs={12} sm={6}>
        <FormControl fullWidth required disabled={disabled}>
          <InputLabel>Pattern Type</InputLabel>
          <Select
            value={formData.patternType}
            label="Pattern Type"
            onChange={(e) =>
              onFormDataChange({
                patternType: e.target.value as 'ALTERNATING' | 'CUSTOM',
              })
            }
          >
            <MenuItem value="ALTERNATING">Alternating (every other week)</MenuItem>
            <MenuItem value="CUSTOM">Custom Pattern</MenuItem>
          </Select>
        </FormControl>
      </Grid>

      {termLengthsDiffer && (
        <Grid item xs={12}>
          <Alert severity="info" data-testid="term-lengths-differ">
            The selected terms differ in length: {termLengthsSentence(selectedTermWeeks)}. A week
            beyond a term&apos;s end is dropped from that term when you save, and you&apos;ll be told which.
          </Alert>
        </Grid>
      )}

      {/* Primary Weeks: Group (for GROUP_INDIVIDUAL and GROUP_BAND) or Individual (for INDIVIDUAL_BAND) */}
      <Grid item xs={12} sm={6}>
        <FormControl fullWidth required disabled={disabled}>
          <InputLabel>{getPrimaryWeeksLabel(combo)}</InputLabel>
          <Select
            multiple
            value={combo === 'INDIVIDUAL_BAND' ? formData.individualWeeks : formData.groupWeeks}
            label={getPrimaryWeeksLabel(combo)}
            SelectDisplayProps={{ 'data-testid': 'primary-weeks-select' } as React.HTMLAttributes<HTMLDivElement>}
            onChange={
              combo === 'INDIVIDUAL_BAND'
                ? handleMultiSelectChange('individualWeeks')
                : handleMultiSelectChange('groupWeeks')
            }
            renderValue={(selected) => selected.join(', ')}
          >
            {weekOptions.map((week) => {
              // For INDIVIDUAL_BAND: individual weeks cannot overlap with group weeks
              // For GROUP_INDIVIDUAL: group weeks cannot overlap with individual weeks
              // For GROUP_BAND: group weeks cannot overlap with band weeks
              const otherWeeks = combo === 'INDIVIDUAL_BAND'
                ? formData.groupWeeks
                : (combo === 'GROUP_INDIVIDUAL' ? formData.individualWeeks : formData.bandWeeks);
              return (
                <MenuItem
                  key={week}
                  value={week}
                  disabled={otherWeeks.includes(week)}
                  // The caption can be long ("not scheduled in Term 4 (class runs weeks 5-8)"):
                  // wrap it on a phone rather than clip it.
                  sx={{ whiteSpace: 'normal' }}
                >
                  {weekLabel(week)}
                </MenuItem>
              );
            })}
          </Select>
        </FormControl>
      </Grid>

      {/* Secondary Weeks: Individual (for GROUP_INDIVIDUAL only) — hidden for band combos */}
      {combo === 'GROUP_INDIVIDUAL' && (
        <Grid item xs={12} sm={6}>
          <FormControl fullWidth required disabled={disabled}>
            <InputLabel>{getSecondaryWeeksLabel(combo)}</InputLabel>
            <Select
              multiple
              value={formData.individualWeeks}
              label={getSecondaryWeeksLabel(combo)}
              onChange={handleMultiSelectChange('individualWeeks')}
              renderValue={(selected) => selected.join(', ')}
            >
              {weekOptions.map((week) => (
                <MenuItem
                  key={week}
                  value={week}
                  disabled={formData.groupWeeks.includes(week)}
                  sx={{ whiteSpace: 'normal' }}
                >
                  {weekLabel(week)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
      )}

      {/* Band Section — shown only when a band combo is selected */}
      {hasBand && (
        <>
          <Grid item xs={12}>
            <Divider sx={{ my: 0.5 }} />
            <Typography variant="caption" color="text.secondary">
              Band Session Configuration
            </Typography>
          </Grid>

          {/* Prompt to select a term first */}
          {!hasTerm && (
            <Grid item xs={12}>
              <Alert severity="info">
                Select a term above to choose a band lesson.
              </Alert>
            </Grid>
          )}

          {/* No-bands warning */}
          {hasTerm && !isBandLessonsLoading && bandLessons.length === 0 && (
            <Grid item xs={12}>
              <Alert severity="warning">
                No band lessons exist for this term. Create a band lesson before using this
                combination.
              </Alert>
            </Grid>
          )}

          {/* Band TBD notice — band combo with no band lesson assigned (e.g. the previous
              band was deleted). The class is valid and saveable, but band sessions are paused
              (not scheduled or billed) until a band is assigned below. */}
          {hasTerm && !isBandLessonsLoading && bandLessons.length > 0 && !formData.bandLessonId && (
            <Grid item xs={12}>
              <Alert severity="info">
                <strong>Band to be assigned.</strong> This class has a band component but no band
                lesson is set. Band sessions are paused (not scheduled or billed) until you choose
                a band lesson below.
              </Alert>
            </Grid>
          )}

          {/* Band lesson selector — only shown once a term is selected */}
          {hasTerm && (
            <Grid item xs={12} sm={6}>
              {isBandLessonsLoading ? (
                <Skeleton variant="rounded" height={56} />
              ) : (
                <FormControl fullWidth required disabled={disabled || bandLessons.length === 0}>
                  <InputLabel>Band Lesson</InputLabel>
                  <Select
                    value={formData.bandLessonId}
                    label="Band Lesson"
                    onChange={handleBandLessonChange}
                  >
                    {bandLessons.map((lesson) => (
                      <MenuItem key={lesson.id} value={lesson.id}>
                        {lesson.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}
            </Grid>
          )}

          {/* Per-term band availability — for a multi-term hybrid class, show which selected
              terms the chosen band exists in. Terms missing the band become "Band: TBD" (the
              backend resolves the same-identity band per term, or leaves it unassigned). */}
          {bandCoverage && bandCoverage.missing.length > 0 && (
            <Grid item xs={12}>
              <Alert severity="warning">
                <Box sx={{ mb: 0.5 }}>
                  This band exists in{' '}
                  <strong>{bandCoverage.present.map((t) => termNames[t] ?? t).join(', ') || 'no selected term'}</strong>.
                  It has no matching band in{' '}
                  <strong>{bandCoverage.missing.map((t) => termNames[t] ?? t).join(', ')}</strong>{' '}
                  — {bandCoverage.missing.length === 1 ? 'that term' : 'those terms'} will be saved as{' '}
                  <strong>Band: TBD</strong> (band sessions paused until you add a matching band there).
                </Box>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {bandCoverage.present.map((t) => (
                    <Chip key={t} label={termNames[t] ?? t} size="small" color="success" variant="outlined" />
                  ))}
                  {bandCoverage.missing.map((t) => (
                    <Chip key={t} label={`${termNames[t] ?? t}: TBD`} size="small" color="warning" variant="outlined" />
                  ))}
                </Box>
              </Alert>
            </Grid>
          )}


          {/* Auto-detected band weeks display — only shown once a term is selected */}
          {hasTerm && <Grid item xs={12} sm={6}>
            <Box
              sx={{
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 1,
                p: 1.5,
                minHeight: 56,
                display: 'flex',
                alignItems: 'center',
                flexWrap: 'wrap',
                gap: 0.5,
              }}
            >
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ width: '100%', mb: 0.5 }}
              >
                Band Weeks (auto-calculated)
              </Typography>
              {isBandWeeksLoading ? (
                <CircularProgress size={16} />
              ) : formData.bandWeeks.length > 0 ? (
                formData.bandWeeks.map((w) => (
                  <Chip key={w} label={`Wk ${w}`} size="small" color="warning" />
                ))
              ) : (
                <Typography variant="caption" color="text.disabled">
                  {formData.bandLessonId
                    ? 'Calculating...'
                    : 'Select a band lesson to see weeks'}
                </Typography>
              )}
            </Box>
          </Grid>}
        </>
      )}

      {/* Individual Slot Duration — the length of a BOOKED clinic, so GROUP_INDIVIDUAL only.
          An INDIVIDUAL_BAND 1:1 week is the class itself and lasts the class Duration (the
          server bills and books it at durationMins); showing this there left it at its 30-min
          default on 45-min classes and read as if it set the price (MNM, 07/10/2026). */}
      {combo === 'GROUP_INDIVIDUAL' && (
        <Grid item xs={12} sm={6}>
          <TextField
            label="Individual Slot Duration"
            type="number"
            value={formData.individualSlotDuration}
            onChange={(e) =>
              onFormDataChange({
                individualSlotDuration: parseInt(e.target.value) || 30,
              })
            }
            fullWidth
            disabled={disabled}
            inputProps={{ min: 15, max: 60, step: 15 }}
            helperText="Duration of each individual slot (mins)"
          />
        </Grid>
      )}

      {/* Booking Deadline — only for GROUP_INDIVIDUAL combo (individual slot bookings) */}
      {combo === 'GROUP_INDIVIDUAL' && (
        <Grid item xs={12} sm={6}>
          <TextField
            label="Booking Deadline"
            type="number"
            value={formData.bookingDeadlineHours}
            onChange={(e) =>
              onFormDataChange({
                bookingDeadlineHours: parseInt(e.target.value) || 24,
              })
            }
            fullWidth
            disabled={disabled}
            inputProps={{ min: 0, max: 168 }}
            helperText="Hours before class that booking closes"
          />
        </Grid>
      )}
    </>
  );
}
